import { AlertDialog, Dialog as RadixDialog } from "radix-ui";
import { X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { errorMessage } from "../lib/api";
import { Button } from "./Button";
import { FormAlert, TextField } from "./fields";

// These controlled dialogs can be opened by several buttons and do not have a Radix Trigger.
// Remember the actual opener before autofocus, so Escape/Cancel returns keyboard users to it.
function useFocusReturn() {
  const opener = useRef<HTMLElement | null>(null);
  return {
    onOpenAutoFocus: () => {
      opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    },
    onCloseAutoFocus: (event: Event) => {
      if (opener.current?.isConnected && !opener.current.matches(':disabled')) {
        event.preventDefault();
        opener.current.focus({ preventScroll: true });
      }
    },
  };
}

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
  /** Block dismissal via Escape/outside click (e.g. while saving). */
  dismissible?: boolean;
  className?: string;
}

/**
 * Modal dialog. Centered enamel card on wide screens; bottom sheet on phones. Body scrolls
 * internally, title and footer actions stay reachable.
 */
export function Dialog({ open, onOpenChange, title, description, children, footer, size = "md", dismissible = true, className }: DialogProps) {
  const focusReturn = useFocusReturn();
  return (
    <RadixDialog.Root open={open} onOpenChange={(next) => (dismissible || next ? onOpenChange(next) : undefined)}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="dialog-backdrop" />
        <RadixDialog.Content
          {...focusReturn}
          className={["dialog", `dialog--${size}`, className ?? ""].join(" ")}
          onEscapeKeyDown={(event) => {
            if (!dismissible) event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (!dismissible) event.preventDefault();
          }}
          onInteractOutside={(event) => {
            if (!dismissible) event.preventDefault();
          }}
        >
          <header className="dialog__header">
            <div className="dialog__titles">
              <RadixDialog.Title className="dialog__title">{title}</RadixDialog.Title>
              {description ? <RadixDialog.Description className="dialog__description">{description}</RadixDialog.Description> : <RadixDialog.Description className="visually-hidden">{typeof title === "string" ? title : "Dialog"}</RadixDialog.Description>}
            </div>
            <button type="button" className="dialog__close" aria-label="Close" onClick={() => dismissible && onOpenChange(false)} disabled={!dismissible}>
              <X size={22} aria-hidden="true" />
            </button>
          </header>
          <div className="dialog__body">{children}</div>
          {footer ? <footer className="dialog__footer">{footer}</footer> : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description: ReactNode;
  confirmLabel: string;
  /** warning = recoverable (trash/remove), danger = irreversible. */
  intent?: "warning" | "danger" | "brand";
  onConfirm: () => Promise<unknown> | unknown;
  /** Require typing this exact text before confirming (rare, catastrophic actions). */
  typedConfirmation?: string;
  children?: ReactNode;
  write?: boolean;
}

/**
 * App-owned confirmation. Cancel receives initial focus. The dialog stays open (busy) until the
 * server confirms; failures are shown inside the dialog with the option to retry or cancel.
 */
export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel, intent = "danger", onConfirm, typedConfirmation, children, write = true }: ConfirmDialogProps) {
  const focusReturn = useFocusReturn();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typed, setTyped] = useState("");
  useEffect(() => {
    if (open) {
      setError(null);
      setTyped("");
      setBusy(false);
    }
  }, [open]);
  const typedOk = !typedConfirmation || typed.trim() === typedConfirmation.trim();

  const run = async () => {
    if (busy || !typedOk) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog.Root open={open} onOpenChange={(next) => (!busy ? onOpenChange(next) : undefined)}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="dialog-backdrop" />
        <AlertDialog.Content {...focusReturn} className="dialog dialog--sm dialog--confirm">
          <header className="dialog__header">
            <div className="dialog__titles">
              <AlertDialog.Title className="dialog__title">{title}</AlertDialog.Title>
              <AlertDialog.Description className="dialog__description">{description}</AlertDialog.Description>
            </div>
          </header>
          <div className="dialog__body">
            {children}
            {typedConfirmation ? (
              <form
                noValidate
                onSubmit={(event) => {
                  event.preventDefault();
                  void run();
                }}
              >
                <TextField
                  label={
                    <>
                      Type <strong className="mono">{typedConfirmation}</strong> to confirm
                    </>
                  }
                  value={typed}
                  onChange={(event) => setTyped(event.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                />
              </form>
            ) : null}
            <FormAlert>{error}</FormAlert>
          </div>
          <footer className="dialog__footer">
            <AlertDialog.Cancel asChild>
              <Button emphasis="outline" intent="neutral" onClick={() => onOpenChange(false)} disabled={busy}>
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <Button emphasis="solid" intent={intent} busy={busy} disabled={!typedOk} write={write} onClick={() => void run()}>
              {confirmLabel}
            </Button>
          </footer>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
